/**
 * Single source of truth for the topic card DOM.
 *
 * Three hydrators in the prototype emit card markup:
 *   - browseController.buildCard                  (Browse grid)
 *   - homeRecommendationHydrator.buildCard        (Home Trending rail)
 *   - homeDiscoveryHydrator.buildRecommendedTopicCard (Home Recommended grid)
 *
 * They were drifting apart: browse had no subtitle, trending used tags
 * as the subtitle, recommended used subtitle. This helper consolidates them
 * behind one builder so visual / structural changes touch one place.
 *
 * Exception (NOT built through this helper):
 *   - Home Recent remains the dedicated .recent-card/play surface. Home
 *     Saved is a configured compact Topic card so reactions share one DOM.
 */

import type { TopicSource } from '../../domain/types/contentTypes';
import type { TopicClassification } from '../../domain/topicClassification';
import type { BrowseCoverBadge } from '../../domain/topicCoverBadge';
import type { Root } from './prototypeTypes';
import { bindTopicCategoryMenuTrigger } from './topicCategoryMenu';
import { observeCompactTextBudget } from './compactTextBudget';
import { coverUrlForTopic, categoriesFromClassification } from '../../domain/topicCover';
import { applyTopicCover } from './topicCoverLoader';
import { topicDislikeButtonMarkup, topicFavoriteButtonMarkup, topicLikeButtonMarkup } from '../../shared/topicReactionMarkup';
import { topicPosterSurfaceMarkup } from '../../shared/topicPosterContent';

export {
	topicDislikeButtonMarkup,
	topicFavoriteButtonMarkup,
	topicLikeButtonMarkup,
	TOPIC_THUMB_DOWN_SVG,
	TOPIC_THUMB_UP_SVG,
} from '../../shared/topicReactionMarkup';

export interface TopicCardInput {
	slug: string;
	title: string;
	/** Origin drives source badges. */
	source?: TopicSource;
	/** Server-resolved, public Browse cover treatment. */
	coverBadge?: BrowseCoverBadge;
	/** Long-form description, the "subtitle" the spec uses as a subtitle. */
	subtitle?: string;
	/** Optional fallback when `subtitle` is empty (e.g., browse's `description`). */
	description?: string;
	category?: string | null;
	classification?: TopicClassification | null;
	coverImageUrl?: string;
	coverGradient?: string | null;
	coverIcon?: string | null;
	chapterCount?: number;
	audioMinutes?: number;
	subscribers?: number;
	openCount?: number;
	favoriteCount?: number;
	dynamicScore?: number;
	recentUpdatedAt?: string | null;
	favoritedAt?: string | null;
	createdAt?: string;
	isNewThisWeek?: boolean;
	isRecent?: boolean;
	isFavorite?: boolean;
	isRecommended?: boolean;
	/** 0 = no vote, 1 = like, -1 = dislike. Per-user. */
	voteValue?: number;
}

export interface TopicCardOpts {
	/** Render the description/subtitle in the subtitle slot (browse + recommended). */
	showSubtitle?: boolean;
	/** Render a small "cover-badge" pill on the cover (home grids only). */
	showCoverBadge?: boolean;
	/** Text for the cover-badge pill. Defaults to "New". */
	coverBadgeText?: string;
	/**
	 * Render an empty auxiliary action slot in the footer. Hydrators can populate
	 * it with a secondary, card-specific action without duplicating the card
	 * builder (for example, AI Create's Submission link).
	 */
	auxiliaryAction?: boolean;
	/**
	 * Compact mode for narrow scroll-list cards (220px): show only the primary
	 * category pill and collapse ALL secondaries into the "+x" trigger. When
	 * falsy (default, grid cards) the first secondary is shown as a 2nd pill.
	 */
	compact?: boolean;
	/**
	 * Footer decides 1-vs-2 visible category pills from the width left over
	 * after the aux action and reaction buttons, instead of a fixed count.
	 * Pills that no longer fit collapse into the same "+x" trigger. Runs after
	 * mount via observeAdaptiveCategoryBudget (see aiCreateViewHydrator).
	 */
	adaptiveCategories?: boolean;
	/**
	 * Shared narrow-screen presentation for completed Topic lists and Home
	 * Saved. Home Recent deliberately keeps its independent play surface.
	 */
	variant?: 'mobile-compact' | 'mobile-poster';
	/**
	 * Some compact surfaces intentionally expose only positive reactions. The
	 * builder owns the action markup so consumers never render then remove a
	 * Dislike control after the fact.
	 */
	reactionActions?: 'all' | 'like-save';
}

const CARET_DOWN_SVG =
	'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"></path></svg>';

export function buildTopicCard(input: TopicCardInput, opts: TopicCardOpts = {}): HTMLElement {
	// The card is a real anchor, not a div[role=link]: crawlers that render JS
	// (and users' middle/Cmd-click) must see a genuine /topics/ link, because
	// <noscript> fallbacks are dropped once JavaScript runs. The delegated
	// click handler preventDefaults plain clicks so SPA navigation still wins.
	const card = document.createElement('a');
	card.className = 'topic-card topic-poster-surface';
	if (opts.variant === 'mobile-compact') card.classList.add('topic-card--mobile-compact');
	if (opts.variant === 'mobile-poster') card.classList.add('topic-poster-surface--cover');
	if (opts.variant === 'mobile-compact' && opts.auxiliaryAction) card.classList.add('topic-card--mobile-compact-has-aux-action');
	card.href = `/topics/${encodeURIComponent(input.slug)}`;
	card.dataset.keyboardAction = 'true';
	card.dataset.id = input.slug;
	if (input.source) card.dataset.source = input.source;
	if (input.category) card.dataset.category = input.category;
	if (input.isRecent) card.dataset.recent = 'true';
	if (input.isRecommended) card.dataset.recommended = 'true';
	if (input.isFavorite) card.setAttribute('data-favorited', '');
	if (typeof input.dynamicScore === 'number') card.dataset.score = String(input.dynamicScore);
	if (typeof input.openCount === 'number') card.dataset.openCount = String(input.openCount);
	if (typeof input.favoriteCount === 'number') card.dataset.favoriteCount = String(input.favoriteCount);
	if (input.recentUpdatedAt) card.dataset.recentAt = input.recentUpdatedAt;
	if (input.favoritedAt) card.dataset.favoritedAt = input.favoritedAt;
	if (input.createdAt) card.dataset.createdAt = input.createdAt;
	card.setAttribute('data-proto-click', `showTopicDetail('${escapeAttr(input.slug)}')`);

	// The server resolves the stable per-topic URL. Category resolution remains
	// a compatibility fallback for callers that have not yet supplied a DTO.
	const gradient = input.coverGradient || 'grad-ai';
	const coverUrl = input.coverImageUrl ?? coverUrlForTopic(categoriesFromClassification(input.classification));
	// A rolling deploy can briefly pair new card code with an older endpoint.
	// Preserve the longstanding GitHub marker in that window without creating a
	// second DOM badge; non-GitHub treatment remains server-authoritative.
	const compactBadge = input.coverBadge ?? (input.source === 'github_trending' ? 'github' : undefined);
	const coverBadge = compactBadge ?? (opts.showCoverBadge ? 'new' : undefined);

	// Footer: one primary category (and a secondary-category menu) + actions.
	const footer = document.createElement('div');
	footer.className = 'topic-footer topic-poster-footer';

	const openCount = typeof input.openCount === 'number' ? input.openCount : 0;
	const voteValue = input.voteValue ?? 0;

	const structuredPrimary = input.classification?.primaryCategory?.label.trim();
	const primaryLabel = structuredPrimary || input.category?.trim() || '';
	const secondaryLabels = (input.classification?.secondaryCategories ?? []).map((category) => category.label.trim()).filter(Boolean);
	// Visible category pills: grid cards show primary + 1 secondary (2 total);
	// compact scroll-list cards show primary only (1). Adaptive cards start
	// with the same 2-pill layout and let syncAdaptiveCategoryBudget re-derive
	// the count from the leftover footer width once mounted. Remaining
	// secondaries collapse into a "+x" trigger whose popover lists only the
	// overflow labels. "+x" is flex:0 0 auto so it never gets pushed off; long
	// pill labels ellipsize via the .pill CSS.
	const adaptiveCategories = opts.adaptiveCategories === true;
	if (adaptiveCategories) footer.classList.add('topic-footer--adaptive');
	const visibleSecondary = opts.compact && !adaptiveCategories ? 0 : 1;
	const visibleSecondaries = secondaryLabels.slice(0, visibleSecondary);
	const overflowLabels = secondaryLabels.slice(visibleSecondary);
	const categorySummary = document.createElement('div');
	categorySummary.className = 'topic-category-summary';
	if (adaptiveCategories) {
		categorySummary.dataset.adaptivePills = '';
		// The initial render keeps the 2-pill grid layout; the full ordered list
		// (primary first) feeds syncAdaptiveCategoryBudget once the footer width
		// is known. The DOM cannot hold it because overflow pills must not paint.
		adaptiveCategoryLabels.set(card, [primaryLabel, ...secondaryLabels].filter(Boolean));
	}
	if (primaryLabel) {
		const pill = document.createElement('span');
		pill.className = 'pill pill-category';
		pill.textContent = primaryLabel;
		categorySummary.appendChild(pill);
	}
	for (const label of visibleSecondaries) {
		const pill = document.createElement('span');
		pill.className = 'pill pill-category';
		pill.textContent = label;
		categorySummary.appendChild(pill);
	}
	if (overflowLabels.length > 0) {
		const more = document.createElement('button');
		more.className = 'topic-category-more';
		more.type = 'button';
		more.setAttribute('aria-label', `Show ${overflowLabels.length} more categor${overflowLabels.length === 1 ? 'y' : 'ies'}`);
		more.innerHTML = `<span>+${overflowLabels.length}</span>${CARET_DOWN_SVG}`;
		categorySummary.appendChild(more);
	}
	footer.appendChild(categorySummary);
	// 可选辅助操作位：各 consumer 在不复制卡片结构的前提下可注入次级动作。
	if (opts.auxiliaryAction) {
		const linkSpan = document.createElement('span');
		linkSpan.className = 'topic-footer-link topic-card-aux-action';
		footer.appendChild(linkSpan);
	}
	const actions = document.createElement('span');
	actions.className = 'topic-actions';
	actions.innerHTML =
		topicLikeButtonMarkup(input.slug, voteValue) +
		(opts.reactionActions === 'like-save' ? '' : topicDislikeButtonMarkup(input.slug, voteValue)) +
		topicFavoriteButtonMarkup(input.slug, Boolean(input.isFavorite));
	footer.appendChild(actions);

	card.innerHTML = topicPosterSurfaceMarkup({
		poster: {
			title: input.title,
			subtitle: opts.showSubtitle ? input.subtitle || input.description || '' : '',
			categoryLabels: [primaryLabel, ...secondaryLabels].filter(Boolean),
			chapterCount: input.chapterCount,
			audioMinutes: input.audioMinutes,
			coverBadge,
			topicId: input.slug,
			voteValue,
			isFavorite: Boolean(input.isFavorite),
			titleTag: 'h4',
			subtitleClass: 'topic-keywords',
			metricVariant: 'topic-card',
			categoryVariant: 'topic-card',
			includeReactions: false,
		},
		coverClass: `topic-cover ${gradient} topic-cover--default`,
		contentClass: 'topic-body',
		categoryMarkup:
			categorySummary.outerHTML + (opts.auxiliaryAction ? '<span class="topic-footer-link topic-card-aux-action"></span>' : ''),
		reactionsMarkup: actions.outerHTML,
		footerClass: footer.className,
	});
	const cover = card.querySelector<HTMLElement>('.topic-poster-cover');
	if (cover) void applyTopicCover(cover, coverUrl);
	const more = card.querySelector<HTMLButtonElement>('.topic-category-more');
	if (more && overflowLabels.length > 0)
		bindTopicCategoryMenuTrigger({ topicId: input.slug, anchorElement: more, categories: overflowLabels });
	if (opts.variant === 'mobile-compact') {
		const renderedTitle = card.querySelector<HTMLElement>('.topic-poster-copy h4');
		if (renderedTitle) syncMobileCompactTextBudget(card, renderedTitle);
	}
	return card;
}

/**
 * A compact Topic row reserves exactly three text lines. CSS can reserve the
 * remaining height, but cannot choose a one- or two-line clamp based on the
 * title's rendered line count. Measure after the card enters the document so
 * the subtitle keeps a real ellipsis in both cases.
 */
function syncMobileCompactTextBudget(card: HTMLElement, title: HTMLElement): void {
	observeCompactTextBudget(card, title, 'topic-card--mobile-compact-title-two-lines');
}

/** Full ordered category labels (primary first) for adaptive-footer cards. */
const adaptiveCategoryLabels = new WeakMap<HTMLElement, string[]>();

// Footer layout constants (topic-cards.css): flex gap between footer groups,
// the 6px negative margin that pulls the aux icon flush with the action
// buttons, and the 6px gap between neighbouring pills.
const FOOTER_GROUP_GAP = 8;
const AUX_TIGHTEN = 6;
const PILL_GAP = 6;

/**
 * Re-derive how many category pills fit in the width left over after the aux
 * action and the reaction buttons. Everything that does not fit collapses
 * into the existing "+x" popover trigger. The primary pill always renders
 * (ellipsis is the fallback when even it cannot fit).
 */
export function syncAdaptiveCategoryBudget(card: HTMLElement): void {
	const summary = card.querySelector<HTMLElement>('.topic-category-summary[data-adaptive-pills]');
	if (!summary || summary.getClientRects().length === 0) return;
	const labels = adaptiveCategoryLabels.get(card);
	if (!labels || labels.length === 0) return;
	const footer = summary.closest<HTMLElement>('.topic-footer');
	if (!footer || footer.clientWidth <= 0) return;

	let reserved = 0;
	const aux = footer.querySelector<HTMLElement>('.topic-card-aux-action');
	if (aux && aux.getClientRects().length > 0) reserved += aux.offsetWidth + FOOTER_GROUP_GAP - AUX_TIGHTEN;
	const actions = footer.querySelector<HTMLElement>('.topic-actions');
	if (actions && actions.getClientRects().length > 0) reserved += actions.offsetWidth + FOOTER_GROUP_GAP;
	const available = Math.max(0, footer.clientWidth - reserved);

	const doc = card.ownerDocument;
	const measurer = doc.createElement('span');
	measurer.setAttribute('aria-hidden', 'true');
	measurer.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;white-space:nowrap;';
	// Keep the measurer in the live summary rather than document.body so the
	// mobile-poster pill rules (including the compact +N chevron) are the same
	// rules used for the actual footer. A body-level measurement is subtly wider
	// because it misses that scoped treatment, which can collapse a label that
	// would in fact fit.
	summary.appendChild(measurer);
	const widths = labels.map((label) => {
		measurer.className = 'pill pill-category';
		measurer.textContent = label;
		return measurer.offsetWidth;
	});
	measurer.className = 'topic-category-more';
	measurer.innerHTML = `<span>+${labels.length}</span>${CARET_DOWN_SVG}`;
	const moreWidth = measurer.offsetWidth;
	measurer.remove();

	let used = 0;
	let visible = 0;
	for (let index = 0; index < labels.length; index++) {
		const pillCost = widths[index] + (index > 0 ? PILL_GAP : 0);
		const moreCost = index < labels.length - 1 ? moreWidth + PILL_GAP : 0;
		// The primary pill is unconditional; every further pill must leave
		// room for the "+x" trigger that would absorb the remaining labels.
		if (index === 0 || used + pillCost + moreCost <= available) {
			used += pillCost;
			visible = index + 1;
		} else {
			break;
		}
	}

	const overflow = labels.slice(visible);
	const fragment = doc.createDocumentFragment();
	for (const label of labels.slice(0, visible)) {
		const pill = doc.createElement('span');
		pill.className = 'pill pill-category';
		pill.textContent = label;
		fragment.appendChild(pill);
	}
	if (overflow.length > 0) {
		const more = doc.createElement('button');
		more.className = 'topic-category-more';
		more.type = 'button';
		more.setAttribute('aria-label', `Show ${overflow.length} more categor${overflow.length === 1 ? 'y' : 'ies'}`);
		more.innerHTML = `<span>+${overflow.length}</span>${CARET_DOWN_SVG}`;
		bindTopicCategoryMenuTrigger({ topicId: card.dataset.id ?? '', anchorElement: more, categories: overflow });
		fragment.appendChild(more);
	}
	summary.replaceChildren(fragment);
}

/**
 * Keep the adaptive pill count correct through the first font/layout
 * settlement and later responsive reflows (grid column count changes with
 * the viewport). Mirrors observeCompactTextBudget's lifecycle: settles after
 * mount, after fonts load, and on card resize; self-disconnects once the
 * card leaves the document.
 */
export function observeAdaptiveCategoryBudget(card: HTMLElement): void {
	const view = card.ownerDocument.defaultView;
	const settle = () => {
		if (!card.isConnected || typeof view?.requestAnimationFrame !== 'function') return;
		view.requestAnimationFrame(() => view.requestAnimationFrame(() => syncAdaptiveCategoryBudget(card)));
	};
	settle();
	void card.ownerDocument.fonts?.ready.then(settle).catch(() => undefined);
	const Observer = view?.ResizeObserver;
	if (!Observer) return;
	const observer = new Observer(() => {
		if (!card.isConnected) {
			observer.disconnect();
			return;
		}
		syncAdaptiveCategoryBudget(card);
	});
	observer.observe(card);
}

function escapeAttr(s: string): string {
	return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}

/**
 * Helper: append a freshly built card to a container, ensuring the
 * optional data attributes used by the filter / scroll-list CSS are
 * preserved. The helpers below are small adapters so the three
 * hydrators can stay nearly one-liners.
 */
export function appendCards(container: Root | HTMLElement, cards: HTMLElement[]) {
	container.replaceChildren(...cards);
}
